import {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import type {FC} from 'react';
import {useQueryClient, useMutation} from '@tanstack/react-query';
import type {FormType} from '@wellstone-solutions/common';
import type {Event} from 'api/v2';
import {groupsEventsDestroy} from 'api/v2';
import {LoadingIndicator} from 'components/shared/LoadingIndicator';

import {Theme} from '@wellstone-solutions/common';

import {
  Box,
  Divider,
  Form as CommonForm,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@wellstone-solutions/web';
import {useStores} from 'hooks/useStores';

import {SchedulerForm} from './SchedulerForm';
import {EventDataForm} from './EventDataForm';
import {ConfirmDisable} from './ConfirmDisable';
import {showAlert, ALERT_TYPES} from 'utils/showAlert';

type PropsType = {
  groupId: string;
  form: FormType<Event>;
  index: number;
  eventsUI: Array<{isVisible: boolean; isOpen: boolean}>;
  helperText: string;
};

export const EventForm: FC<PropsType> = ({
  groupId,
  form,
  index,
  eventsUI,
  helperText,
}: PropsType) => {
  const [showScheduler, setShowScheduler] = useState(eventsUI[index].isOpen);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showDeleteLoader, setShowDeleteLoader] = useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const deleteEventMutation = useMutation({
    mutationFn: ({groupId, groupEventId}) =>
      groupsEventsDestroy(groupId, groupEventId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: ['group-events']});
      // TODO: Replace these two fetches with RQ expiration pattern
      await programStore.getAllPrograms();
      await calendarStore.getAllCalendarEvents();

      navigate(`/groups/${groupId}`);
    },
  });

  const {meStore, programStore, calendarStore} = useStores();

  const isEditMode = Boolean(form.initialValues.id);

  useEffect(() => {
    if (isEditMode) {
      setShowScheduler(true);
    }
  }, [form.initialValues.id]);

  const handleOnConfirm = useCallback(async () => {
    if (isEditMode) {
      // Edit Mode: send a DELETE request
      setShowDeleteLoader(true);
      try {
        await deleteEventMutation.mutateAsync({
          groupId,
          groupEventId: form.values.groupEventId,
        });
      } catch (error) {
        console.error('Error removing event:', error);
        showAlert({
          type: ALERT_TYPES.ERROR,
          message: 'Failed to remove the event. Please try again.',
        });
      } finally {
        setShowDeleteLoader(false);
        form.resetForm();
        setShowScheduler(false);
      }
    } else {
      // New Mode: just reset the form
      form.resetForm();
      setShowScheduler(false);
    }
  }, []);

  const handleEnableSchedule = (event: Event) => {
    if (event.target.checked) {
      // show this form, but then add a new form to forms array
      // TODO: For the edit version, we need a prompt to confirm removal
      setShowScheduler(true);
      form.setFieldValue('isRecurring', true);
      form.setFieldValue('createdBy', meStore.me.id);
      form.setFieldValue('owner', meStore.me.id);
    } else {
      // form is dirty and touched OR has initial ID (edit mode), show confirm
      if (
        (form.dirty && Object.keys(form.touched).length > 0) ||
        Boolean(form.initialValues.id)
      ) {
        setShowRemoveConfirm(true);
      } else {
        form.resetForm();
        setShowScheduler(false);
      }
    }
  };

  return (
    <Box sx={{display: eventsUI[index].isVisible ? 'initial' : 'none'}}>
      <CommonForm form={form} id={`group-event-form-${index}`}>
        <Paper
          elevation={2}
          sx={{
            p: {
              xs: 2,
              sm: 3,
            },
            mt: 2,
            borderRadius: 3,
          }}>
          <Typography sx={{fontWeight: 'bold', fontSize: 18}}>
            Meeting Details
          </Typography>
          {!isEditMode && <Typography>{helperText}</Typography>}
          <Stack
            direction="column"
            sx={{
              mb: 2,
              mt: 2,
              border: `1px solid ${
                showScheduler ? Theme.colorPalette.primary : '#333333'
              }`,

              borderRadius: 3,
              backgroundColor: showScheduler
                ? Theme.colorPalette.primary4
                : '#f9f9f9',
            }}>
            <Stack
              direction="row"
              sx={{ml: 2, mt: 1, mb: 1, alignItems: 'center'}}>
              <Typography sx={{fontSize: 16, fontWeight: 'bold'}}>{`${
                !showScheduler ? 'Enable ' : ''
              }Group Schedule`}</Typography>
              <Switch
                data-testid={`group-event-scheduler-switch-${index}`}
                label=""
                checked={showScheduler}
                onChange={handleEnableSchedule}
                sx={{ml: 5}}
              />
            </Stack>
            {showDeleteLoader && <LoadingIndicator />}

            {!showDeleteLoader && (
              <Stack
                direction="column"
                sx={{
                  display: showScheduler ? 'flex' : 'none',
                  ml: 2,
                  mr: 2,
                  mt: 2,
                }}>
                <SchedulerForm form={form} />
                <Divider sx={{mt: 3, mb: 1}} />
                <EventDataForm form={form} />
              </Stack>
            )}
          </Stack>
        </Paper>
      </CommonForm>
      <ConfirmDisable
        isEditMode={form.initialValues?.id.length > 0}
        open={showRemoveConfirm}
        setOpen={setShowRemoveConfirm}
        onConfirm={handleOnConfirm}
      />
    </Box>
  );
};
